import classnames from 'classnames';
import { ReactElement } from 'react';
import styled from 'styled-components/macro';

import { PopperPlacementType } from '@mui/material/Popper';
import MUITooltip from '@mui/material/Tooltip';

import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { useTranslation } from 'ev-i18n';

import Icon from 'ev-components/Icon';
import { EVColors } from 'ev-theme/styles';

export enum TooltipVariant {
  Light = 'light',
  Dark = 'dark',
}

export type BaseTooltipProps = {
  id: string;
  text: string | ReactElement;
  // Keyboard shortcut shown as a secondary second line.
  shortcut?: string;
  variant?: TooltipVariant;
  arrowPlacement?: PopperPlacementType;
  sx?: { [key: string]: string | number };
  maxWidth?: string;
  offsetX?: string;
  padding?: string;
  className?: string;
  onClose?: () => void;
  closeButtonLabel?: string;
};

export interface TooltipProps extends BaseTooltipProps {
  children: ReactElement;
  open?: boolean;
}

const Tooltip = ({
  id,
  arrowPlacement,
  variant = TooltipVariant.Light,
  text,
  shortcut,
  open,
  children,
  sx,
  maxWidth = '139px',
  offsetX,
  padding,
  className,
  onClose,
  closeButtonLabel,
}: TooltipProps) => {
  const { t } = useTranslation();

  const body = shortcut ? (
    <>
      <TooltipLabel>{text}</TooltipLabel>
      <TooltipShortcut $variant={variant}>{shortcut}</TooltipShortcut>
    </>
  ) : (
    text
  );

  const content = onClose ? (
    <TooltipContent>
      <TooltipText>{body}</TooltipText>
      <CloseButton
        aria-label={closeButtonLabel || t('Close')}
        data-testid={`${id}-close`}
        onClick={onClose}
        type="button"
      >
        <Icon color={closeIconColors[variant]} icon={faTimes} />
      </CloseButton>
    </TooltipContent>
  ) : (
    body
  );

  return (
    <MUITooltip
      PopperProps={{
        placement: arrowPlacement,
      }}
      componentsProps={{
        tooltip: {
          className: classnames(variant, className),
        },
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              marginLeft: offsetX,
              padding,
              maxWidth,
            },
            ...sx,
            maxWidth,
          },
          className: 'chromatic-ignore',
        },
      }}
      data-testid={id}
      open={open}
      title={content}
      arrow
    >
      {children}
    </MUITooltip>
  );
};

export default Tooltip;

const shortcutColors: Record<TooltipVariant, string> = {
  [TooltipVariant.Light]: EVColors.text.subtle,
  [TooltipVariant.Dark]: EVColors.mercury,
};

// Matches each variant's text color (see ev-theme/components/MuiTooltip).
const closeIconColors: Record<TooltipVariant, string> = {
  [TooltipVariant.Light]: EVColors.asphalt,
  [TooltipVariant.Dark]: EVColors.white,
};

const TooltipLabel = styled.span`
  display: block;
  text-align: center;
`;

const TooltipShortcut = styled.span<{ $variant: TooltipVariant }>`
  display: block;
  text-align: center;
  color: ${({ $variant }) => shortcutColors[$variant]};
`;

const TooltipContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const TooltipText = styled.span`
  flex: 1 1 auto;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.85;

  :hover {
    opacity: 1;
  }
`;
